import { IIIFImageRequestUrlOptions, Resource } from '@aipb/gulliver/browser/model';
import { isArray, stripEndingSlash, uniqueId } from '@aipb/shared/util/other';
import { Annotation as AnnotationV2, Canvas, Manifest as ManifestV2 } from '@iiif/presentation-2';
import { Canvas as CanvasV2 } from '@iiif/presentation-2/resources/canvas';
import { Annotation, InternationalString, Manifest as ManifestV3 } from '@iiif/presentation-3';
import { ImageInformationResponse } from 'ol/format/IIIFInfo';
import { shorten, slugify } from '@aipb/shared/util/string';

export function getLabel(string: InternationalString, language?: string) {
  if (!string) {
    return '';
  }

  if (language in string) {
    return string[language][0];
  }

  return string[Object.keys(string)[0]][0];
}

export function getIIIFImageFullUrlV2(image: AnnotationV2): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.resource['service']['@id'])}/full/full/0/default.jpg`;
}

export function getIIIFImageFullUrl(image: Annotation): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.body['service'][0]['id'])}/full/full/0/default.jpg`;
}

export function getThumbUrlV2(image: AnnotationV2): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.resource['service']['@id'])}/full/150,/0/default.jpg`;
}

export function getThumbUrlV2ByHeight(image: AnnotationV2, height: number): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.resource['service']['@id'])}/full/,${height}/0/default.jpg`;
}

export function getIIIFImageThumbUrl(image: Annotation): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.body['service'][0]['id'])}/full/150,/0/default.jpg`;
}

export function getIIIFImageByWidthUrlV2(image: AnnotationV2, width: number): string | null {
  if (!image) return null;

  return iiifImageRequestUrlV2(image, {
    size: `${width},`
  });
}

export function getIIIFImageByWidthUrl(image: Annotation, width: number): string | null {
  if (!image) return null;

  return iiifImageRequestUrlV3(image, {
    size: `${width},`
  });
}

export function getIIIFImageByHeightUrl(image: Annotation, height: number): string | null {
  if (!image) return null;

  return `${stripEndingSlash(image.body['service'][0]['id'])}/full/,${height}/0/default.jpg`;
}

export function getIIIFImageWidthV2(image: AnnotationV2, canvas: Canvas): number {
  if (!image) return 0;

  return image.resource['width'] || image.resource['service'].width || canvas.width;
}

export function getIIIFImageHeightV2(image: AnnotationV2, canvas: Canvas): number {
  if (!image) return 0;

  return image.resource['height'] || image.resource['service'].height || canvas.height;
}

export function getIIIFImageWidth(image: Annotation): number {
  if (!image) return 0;

  return image.body['width'];
}

export function getIIIFImageHeight(image: Annotation): number {
  if (!image) return 0;

  return image.body['height'];
}

export function getIIIFImageThumbnail(image: Annotation, info: ImageInformationResponse): string {
  if (image.thumbnail) {
    return image.thumbnail[0].id;
  }

  const qualities = info['profile'][1].qualities;
  const formats = info['profile'][1].formats;

  const defaultFormat = formats ? formats[0] : 'jpg';
  const defaultQuality = qualities ? (qualities.includes('thumb') ? 'thumb' : qualities[0]) : 'default';

  return `${stripEndingSlash(image.id)}/full/150,/0/${defaultQuality}.${defaultFormat}`;
}

function buildDefaultImageRequestUrlOptions(options: IIIFImageRequestUrlOptions = {}): IIIFImageRequestUrlOptions {
  const { region, size, rotation, quality, format } = options;

  return {
    region: region || 'full',
    size: size || 'full',
    rotation: rotation || 0,
    quality: quality || 'default',
    format: format || 'jpg'
  };
}

export function buildImageRequestUrl(url: string, options: IIIFImageRequestUrlOptions): string {
  const { region, size, rotation, quality, format } = buildDefaultImageRequestUrlOptions(options);
  return `${stripEndingSlash(url)}/${region}/${size}/${rotation}/${quality}.${format}`;
}

export function iiifImageRequestUrlV2(image: AnnotationV2, options: IIIFImageRequestUrlOptions = {}) {
  return buildImageRequestUrl(image.resource['service']['@id'], options);
}

export function iiifImageRequestUrlV3(image: Annotation, options: IIIFImageRequestUrlOptions = {}) {
  return buildImageRequestUrl(image.body['service'][0]['id'], options);
}

function isIIIFVersion(manifest: ManifestV2 | ManifestV3, version: number) {
  const context: string = isArray(manifest['@context']) ? manifest['@context'][0] : <string>manifest['@context'];

  const regex = new RegExp('^(https?:)?\\/\\/iiif\\.io\\/api\\/presentation\\/' + version, 'g');
  const match = context.match(regex);
  return match && match.length > 0;
}

export function isIIIFv2(data: ManifestV2 | ManifestV3): boolean {
  return isIIIFVersion(data, 2);
}

export function isIIIFv3(data: ManifestV2 | ManifestV3): boolean {
  return isIIIFVersion(data, 3);
}

export function validateIIIFType(data: ManifestV2 | ManifestV3): boolean {
  return (data['@type'] && data['@type'] === 'sc:Manifest') || (data['type'] && data['type'] === 'Manifest');
}

export function getNibbleIdFromResource(resource: Resource): number {
  if (!resource) return null;

  const url = new URL(resource['@id']);
  const nid = url.searchParams.get('nid');
  return nid ? +nid : null;
}

export function hasCanvasAnnotations(canvas: CanvasV2) {
  if (!canvas) return false;

  if (!canvas.otherContent) return false;

  return (
    canvas?.otherContent.filter(
      (oc) =>
        oc?.['service']?.['@context'] === 'https://manuscriptorium.com/ns/gulliver/personal-content' ||
        oc?.['service']?.['@context'] === 'https://aipberoun.cz/ns/gulliver/personal-content'
    ).length > 0
  );
}

export function generateImageDownloadName(project: string, manifestLabel: string, canvasLabel: string, suffix = '') {
  let items = [project];

  if (manifestLabel && canvasLabel) {
    items = [...items, slugify(shorten(manifestLabel, 15)), slugify(shorten(canvasLabel, 15))];
  } else {
    items = [...items, uniqueId()];
  }

  if (suffix) {
    items.push(suffix);
  }

  return items.join('_');
}
